/**
 * "Start from a booth that's live" — the starter rail.
 *
 * Replaces the text starter chips with the thing the chips described. A
 * creator picks with their eyes, so a phrase like "Y2K chrome & pixel, candy
 * pop colors" is a worse offer than a picture of the booth it produced. Each
 * card carries the booth's real welcome screen and real URL: tapping seeds the
 * composer, and the slug opens the booth itself so nothing here is a mockup.
 *
 * Falls back to the region's text chips when the showcase is empty, which is
 * also the state before anything has been curated.
 */

import React from 'react'
import type { ShowcaseBooth } from '../onboardingApi'
import { publicBoothUrl, publicBoothHostLabel } from '../publicOrigin'

interface BoothRailProps {
  booths: ShowcaseBooth[]
  /** Text chips used when there's nothing curated to show. */
  starterPrompts: string[]
  onPick: (prompt: string) => void
  activeSlug: string | null
}

export const BoothRail: React.FC<BoothRailProps> = ({
  booths,
  starterPrompts,
  onPick,
  activeSlug
}) => {
  if (booths.length === 0) {
    return (
      <div className="space-y-2">
        <p className="text-center text-xs uppercase tracking-wide text-whiteout/40">
          Try one of these
        </p>
        <div className="flex flex-wrap justify-center gap-2">
          {starterPrompts.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => onPick(s)}
              className="rounded-full border border-white/10 bg-white/[0.04] px-3.5 py-1.5 text-xs text-whiteout/70 transition-colors hover:border-blueSparkle/60 hover:text-whiteout"
            >
              {s}
            </button>
          ))}
        </div>
      </div>
    )
  }

  return (
    <section className="space-y-2">
      <p className="text-xs uppercase tracking-wide text-whiteout/40">
        Start from a booth that&apos;s live
      </p>
      {/* Horizontal scroll rather than wrap: the rail should read as a strip
          you can push through, and it keeps the hero from growing taller as
          more booths get curated. */}
      <div className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-2 soft-scrollbar">
        {booths.map((b) => (
          <div key={b.slug} className="w-24 shrink-0">
            <button
              type="button"
              onClick={() => onPick(b.prompt || b.title)}
              title={`Start from ${b.title}`}
              className={`block w-full overflow-hidden rounded-xl border transition-colors ${
                activeSlug === b.slug
                  ? 'border-blueSparkle'
                  : 'border-white/10 hover:border-white/30'
              }`}
            >
              <img
                src={b.thumbnail}
                alt={b.title}
                loading="lazy"
                draggable={false}
                style={{ aspectRatio: '540 / 960', backgroundColor: b.palette.backgroundColor }}
                className="w-full object-cover"
              />
            </button>
            <a
              href={publicBoothUrl(b.slug)}
              target="_blank"
              rel="noreferrer"
              className="mt-1 block truncate font-mono text-[9.5px] text-whiteout/40 transition-colors hover:text-whiteout/80"
              title={`Open ${publicBoothHostLabel()}/${b.slug}`}
            >
              /{b.slug}
            </a>
          </div>
        ))}
      </div>
    </section>
  )
}

export default BoothRail
